import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';
import { forgotPassword } from '../utils/api';
import { useToast } from '../context/ToastContext';

const LoginPage = () => {
        const [email, setEmail] = useState('');
        const [password, setPassword] = useState('');
        const [error, setError] = useState('');
        const [showForgotPassword, setShowForgotPassword] = useState(false);
        const [forgotEmail, setForgotEmail] = useState('');
        const [forgotError, setForgotError] = useState('');
        const [forgotSuccess, setForgotSuccess] = useState('');
        const navigate = useNavigate();
        const [searchParams] = useSearchParams();
        const { login } = useContext(AuthContext);
        const { showToast } = useToast();

        const handleLogin = async (e) => {
                e.preventDefault();
                setError('');

                try {
                        await login(email, password);

                        // Get the returnUrl from query params, default to '/' if not present
                        const returnUrl = searchParams.get('returnUrl') || '/';

                        // Validate that returnUrl is a relative path (security: prevent open redirect)
                        const isValidReturnUrl = returnUrl.startsWith('/') && !returnUrl.startsWith('//');

                        navigate(isValidReturnUrl ? decodeURIComponent(returnUrl) : '/');
                } catch (error) {
                        setError(error.message || 'An error occurred. Please try again later.');
                }
        };

        const handleForgotPassword = async (e) => {
                e.preventDefault();
                setForgotError('');
                setForgotSuccess('');

                try {
                        const response = await forgotPassword(forgotEmail);
                        if (response.ok) {
                                setForgotSuccess('If an account exists with this email, a password reset link has been sent!');
                                showToast('success', 'If an account exists with this email, a password reset link has been sent!');
                                setTimeout(() => {
                                        setShowForgotPassword(false);
                                        setForgotEmail('');
                                        setForgotSuccess('');
                                }, 2000);
                        } else {
                                setForgotError(error.message || 'Failed to send reset email. Please try again.');
                        }
                } catch (error) {
                        setForgotError(error.message || 'An error occurred. Please try again.');
                }
        };

        return (
                <div className="flex items-center justify-center h-screen bg-bg-page">
                        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                                <div className="text-center">
                                        <img src="/images/logo_text_color.png" alt="CleverPlant" className="h-10 mx-auto mb-2" />
                                </div>
                                <form className="space-y-6" onSubmit={handleLogin}>
                                        <div>
                                                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                                                        Email
                                                </label>
                                                <input
                                                        id="email"
                                                        data-testid="login-email"
                                                        name="email"
                                                        type="email"
                                                        autoComplete="email"
                                                        required
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                />
                                        </div>
                                        <div>
                                                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                                        Password
                                                </label>
                                                <input
                                                        id="password"
                                                        data-testid="login-password"
                                                        name="password"
                                                        type="password"
                                                        autoComplete="current-password"
                                                        required
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                />
                                        </div>
                                        {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
                                        <div>
                                                <button
                                                        type="submit"
                                                        data-testid="login-submit"
                                                        className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                                                >
                                                        Sign in
                                                </button>
                                        </div>
                                        <div className="text-center space-y-2">
                                                <button
                                                        type="button"
                                                        onClick={() => setShowForgotPassword(true)}
                                                        data-testid="login-forgot-open"
                                                        className="text-sm text-brand hover:text-brand/80"
                                                >
                                                        Forgot your password?
                                                </button>
                                                <p className="text-sm text-gray-500">
                                                        Don't have an account?{' '}
                                                        <Link to="/register" data-testid="login-register-link" className="text-brand hover:text-brand/80 font-medium">
                                                                Create one
                                                        </Link>
                                                </p>
                                        </div>
                                </form>
                        </div>

                        <Modal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)}>
                                <form onSubmit={handleForgotPassword}>
                                        <h3 className="text-lg font-bold mb-4">Reset Password</h3>
                                        <p className="text-sm text-gray-600 mb-4">
                                                Enter your email address and we'll send you a link to reset your password.
                                        </p>
                                        <div className="mb-4">
                                                <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Email Address
                                                </label>
                                                <input
                                                        id="forgot-email"
                                                        data-testid="forgot-email"
                                                        type="email"
                                                        required
                                                        value={forgotEmail}
                                                        onChange={(e) => setForgotEmail(e.target.value)}
                                                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand focus:border-brand"
                                                        placeholder="your@email.com"
                                                />
                                        </div>
                                        {forgotError && <p data-testid="forgot-error" className="text-sm text-red-600 mb-4">{forgotError}</p>}
                                        {forgotSuccess && <p data-testid="forgot-success" className="text-sm text-green-600 mb-4">{forgotSuccess}</p>}
                                        <div className="flex justify-end space-x-4">
                                                <button
                                                        type="button"
                                                        onClick={() => setShowForgotPassword(false)}
                                                        data-testid="forgot-cancel"
                                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="submit"
                                                        data-testid="forgot-submit"
                                                        className="px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md hover:bg-brand/90"
                                                >
                                                        Send Reset Link
                                                </button>
                                        </div>
                                </form>
                        </Modal>
                </div>
        );
};

export default LoginPage;
